import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Image, StyleSheet, Text, View } from 'react-native';

import { ChipCoinIcon } from '../../../assets';
import { testProps } from '../../../config';
import { useDevice } from '../../../utils/hooks/useDevice';
import { playSound } from '../../../utils/sound';
import { CtaButton } from '../common/CtaButton';
import { TouchableOpacity } from '../common/Touchable';
import { redesignPanelColors } from './redesignPanelChrome';
import SvaraModal from './SvaraModal';

interface NotEnoughChipsModalProps {
	visible: boolean;
	onClose: () => void;
	onBuy: () => void;
}

const NotEnoughChipsModal = ({ visible, onClose, onBuy }: NotEnoughChipsModalProps) => {
	const { t } = useTranslation();
	const { isDesktop } = useDevice();
	const content = t('T_SITE_MODAL_NOT_ENOUGH_CHIPS_CONTENT').replace(/<br\s*\/?>/gi, '\n');

	useEffect(() => {
		if (visible) playSound('Notification'); // v1 modal.not.enough.chips.component
	}, [visible]);

	return (
		<SvaraModal visible={visible} onClose={onClose} contentWrapperStyles={styles.contentWrapper}>
			<View style={[styles.panel, { width: isDesktop ? 400 : 320 }]}>
				<Text style={[styles.title, isDesktop ? styles.titleDesktop : styles.titleMobile]}>
					{t('T_SITE_MODAL_NOT_ENOUGH_CHIPS_TITLE')}
				</Text>
				<Text style={[styles.message, isDesktop ? styles.messageDesktop : styles.messageMobile]}>
					{content}
				</Text>
				<TouchableOpacity
					onPress={onBuy}
					activeOpacity={0.85}
					accessibilityElementsHidden
					importantForAccessibility='no-hide-descendants'
				>
					<Image source={ChipCoinIcon} resizeMode='contain' style={styles.chipBadge} />
				</TouchableOpacity>
				<View {...testProps('cta-not-enough-chips-buy')}>
					<CtaButton
						label={t('T_SITE_MODAL_NOT_ENOUGH_CHIPS_BUY').toUpperCase()}
						onPress={onBuy}
						style={styles.buyCta}
						labelStyle={styles.buyLabel}
					/>
				</View>
			</View>
		</SvaraModal>
	);
};

const styles = StyleSheet.create({
	contentWrapper: {
		paddingTop: 0,
		paddingHorizontal: 0,
		gap: 0,
	},
	panel: {
		alignItems: 'center',
		paddingHorizontal: 24,
		paddingTop: 48,
		paddingBottom: 28,
	},
	title: {
		fontFamily: 'Sofia Sans',
		color: redesignPanelColors.title,
		fontWeight: '800',
		textAlign: 'center',
		marginBottom: 12,
		textShadowColor: 'rgba(255, 114, 0, 0.6)',
		textShadowOffset: { width: 0, height: 0 },
		textShadowRadius: 6,
	},
	titleDesktop: {
		fontSize: 32,
	},
	titleMobile: {
		fontSize: 26,
	},
	message: {
		fontFamily: 'Sofia Sans',
		fontWeight: '500',
		color: redesignPanelColors.white,
		textAlign: 'center',
		paddingHorizontal: 28,
	},
	messageDesktop: {
		fontSize: 19,
		lineHeight: 28,
	},
	messageMobile: {
		fontSize: 16,
		lineHeight: 23,
	},
	chipBadge: {
		width: 108,
		height: 108,
		marginTop: 20,
		marginBottom: 12,
	},
	buyCta: {
		width: 168,
		minHeight: 40,
	},
	buyLabel: {
		fontSize: 15,
	},
});

export default NotEnoughChipsModal;
